import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X as CloseIcon } from 'lucide-react';
import { MimiqueIcon, MimiqueLockup } from './MimiqueLogo';
import { useCountdown } from '../lib/useCountdown';
import { CHALLENGE_CLOSES } from '../lib/challengeDates';

// ── Design-system constants ──────────────────────────────────────────────────
// Shared by every public page so the landing page and the legal pages read as
// one site. Landing.js re-exports nothing of its own: it imports from here.
export const C = {
    darkest: '#0a1610',
    deep: '#1a5235',
    primary: '#2d7a4f',
    mid: '#3e8f63',
    bright: '#4da673',
    light: '#7ab896',
    paleBg: '#eef5f0',
    faqBg: '#f0f9f3',
    faqBorder: '#bfe4cc',
    borderLight: '#dcebe1',
    borderLight2: '#e2ece5',
    onDarkPrimary: '#e0f0e8',
    onDarkMuted: 'rgba(243,249,245,0.78)',
    footerLink: '#a9d4bc',
    bodyOnWhite: '#5b7c68',
    bodyOnWhite2: '#4a6357',
};

export const F = {
    heading: "'Nunito Sans', sans-serif",
    brand: "'Manrope', sans-serif",
    body: "'Inter', sans-serif",
};

// Primary CTA, matched to the app's own "Start Practice Session" button
// (bg-accent / white / font-bold / rounded-full / shadow-md) so the landing
// page and the app read as one product. Defined once and spread into each CTA.
export const CTA_PRIMARY = {
    // inline-flex + lineHeight 1 keeps the label and the hover arrow on the
    // same optical centre; without it the arrow sits on the text baseline and
    // pushes the button's box taller at the top.
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    border: 'none',
    cursor: 'pointer',
    background: '#4da874',
    color: '#ffffff',
    fontFamily: F.body,
    fontSize: 17,
    fontWeight: 700,
    borderRadius: 9999,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

export const SOCIALS = [
    {
        label: 'Instagram',
        href: 'https://www.instagram.com/leanne.in.france/',
        icon: (
            <>
                <rect x="3" y="3" width="18" height="18" rx="5" stroke="#7ab896" strokeWidth="1.8" />
                <circle cx="12" cy="12" r="4" stroke="#7ab896" strokeWidth="1.8" />
                <circle cx="17.2" cy="6.8" r="1.1" fill="#7ab896" />
            </>
        ),
    },
    {
        label: 'TikTok',
        href: 'https://www.tiktok.com/@mimique_french',
        icon: <path d="M16 3c.5 2.6 2.1 4.2 4.5 4.4v3.1c-1.6.1-3-.4-4.5-1.4v6.6c0 3.5-2.8 5.8-5.9 5.3-2.5-.4-4.3-2.7-4.1-5.3.2-2.6 2.6-4.6 5.2-4.3v3.1c-1-.2-2 .4-2.2 1.5-.2 1.1.6 2.1 1.7 2.2 1.2.1 2.2-.8 2.2-2V3h3.1Z" fill="#7ab896" />,
    },
    {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/company/mimique-app',
        icon: (
            <>
                <rect x="3" y="3" width="18" height="18" rx="4" stroke="#7ab896" strokeWidth="1.8" />
                <circle cx="8" cy="8.5" r="1.2" fill="#7ab896" />
                <path d="M8 11.5v6M12 17.5v-3.8c0-1.4 1-2.4 2.2-2.4 1.2 0 1.8 1 1.8 2.4v3.8" stroke="#7ab896" strokeWidth="1.8" strokeLinecap="round" />
            </>
        ),
    },
    {
        label: 'Facebook',
        href: 'https://www.facebook.com/profile.php?id=61595591071679',
        icon: (
            <>
                <circle cx="12" cy="12" r="9" stroke="#7ab896" strokeWidth="1.8" />
                <path d="M13.5 21v-6.5h2.2l.3-2.6h-2.5V10.2c0-.75.2-1.26 1.28-1.26h1.37V6.6c-.24-.03-1.05-.1-1.99-.1-1.97 0-3.32 1.2-3.32 3.4v1.9H8.5v2.6h2.3V21" stroke="#7ab896" strokeWidth="1.8" strokeLinejoin="round" />
            </>
        ),
    },
];

// Section links live here so the header and its burger panel can never drift
// apart, and so a page that isn't the landing page still points at the right
// anchors.
const NAV_LINKS = [
    ['steps', 'How it works'],
    ['features', "Who it's for"],
    ['pricing', 'How much?'],
    ['faq', 'FAQ'],
    // A third entry is a page of its own rather than a section of this one.
    ['blog', 'Blog', '/blog'],
];

/**
 * Scroll the landing page to a section, rather than leaving it to the browser.
 *
 * A plain #hash click only works if the section is already in the page and the
 * hash actually changes. Neither holds for the blog: it is rendered only once
 * the posts have been fetched, so an early click lands nowhere, and every
 * click after that is a no-op because the address bar already says #blog.
 * So the section is waited for, briefly, and scrolled to by hand.
 */
const scrollToSection = (id) => {
    let attempts = 0;
    const look = () => {
        const target = document.getElementById(id);
        if (target) {
            target.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
            // Kept in the address bar so a reload or a share still lands here,
            // without pushing a history entry the back button has to undo.
            window.history.replaceState(null, '', `#${id}`);
            return;
        }
        // Roughly three seconds, which covers a slow load of the posts.
        if (attempts++ < 30) setTimeout(look, 100);
    };
    // A frame late, so the burger panel has already closed and the section is
    // measured where it will actually be, not where it was under the open menu.
    requestAnimationFrame(look);
};

// Hover and nav behaviour used by both the header and the footer. Rendered by
// SiteHeader, which every public page includes, so the rules are present
// wherever a .mq-cta or .mq-social is.
const ChromeStyles = () => (
    <style>{`
        /* Hover lift on CTAs and nav pill, per the design system. */
        .mq-cta { transition: transform 0.2s ease; display: inline-block; }
        .mq-cta:hover { transform: translateY(-4px) scale(1.05); }
        .mq-navpill { transition: transform 0.2s ease; }
        .mq-navpill:hover { transform: translateY(-3px) scale(1.05); }
        .mq-navlink { transition: color 0.2s ease; }
        .mq-navlink:hover { color: #b8ecce !important; }
        .mq-social { transition: transform 0.2s ease, background 0.2s ease; }
        .mq-social:hover { transform: translateY(-3px); background: rgba(255,255,255,0.12) !important; }
        /* Footer links had no hover at all, so only the underlined website
           credit read as clickable. Same mint as the nav links on hover. */
        .mq-footlink { transition: color 0.2s ease; }
        .mq-footlink:hover { color: #b8ecce !important; }

        /* Card hover lift, shared by the blog cards, the pricing tiers and the
           FAQ rows so all three behave the same and change in one place.

           It lifts with the translate property rather than transform on
           purpose: the landing page's scroll reveal animates transform, and
           the two would otherwise fight over the same property and the same
           transition. The class is doubled so this transition outranks the
           reveal's, and it restates the reveal's own opacity and transform
           timings so those still run at their original speed.

           box-shadow is !important because the pricing cards set it inline. */
        .mq-lift.mq-lift {
            transition: translate 160ms ease,
                        box-shadow 160ms ease,
                        opacity 0.7s cubic-bezier(0.22, 0.61, 0.36, 1),
                        transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1);
        }
        .mq-lift:hover { translate: 0 -3px; box-shadow: 0 18px 40px -24px rgba(10,22,16,0.45) !important; }
        /* The recommended tier already carries a deep shadow, so it lifts into
           a stronger one rather than dropping to the standard card shadow. */
        .mq-lift--strong:hover { box-shadow: 0 32px 60px -20px rgba(28,76,49,0.45) !important; }

        /* The oversized logo watermark behind the hero and the closing band.
           Defined here rather than on the landing page because the closing
           band is used by the blog posts too. */
        .mq-motif {
            position: absolute;
            top: -110px;
            right: -110px;
            opacity: 0.07;
            pointer-events: none;
            z-index: 0;
            line-height: 0;
        }
        /* Smaller and tucked further into the corner on mobile so it frames
           the headline rather than sitting behind it. */
        @media (max-width: 768px) {
            .mq-motif { top: -80px; right: -100px; opacity: 0.09; }
            .mq-motif svg { width: 300px; height: 300px; }
        }
        /* The rule above outranks the reveal's own reduced-motion opt-out, so
           it has to repeat it or these cards would keep animating for someone
           who asked the browser for less motion. */
        @media (prefers-reduced-motion: reduce) {
            .mq-lift.mq-lift { transition: none; }
        }

        /* Arrow that slides in on CTA hover. inline-flex + line-height 1
           keeps it optically centred against the label rather than
           sitting on the text baseline. */
        .mq-arrow {
            display: inline-flex;
            align-items: center;
            line-height: 1;
            max-width: 0;
            opacity: 0;
            overflow: hidden;
            color: currentColor;
            transform: translateX(-4px);
            transition: max-width 0.2s ease, opacity 0.2s ease, transform 0.2s ease, margin-left 0.2s ease;
        }
        .mq-arrowbtn:hover .mq-arrow {
            max-width: 22px;
            opacity: 1;
            margin-left: 8px;
            transform: translateX(0);
        }

        /* Nav: drop the section links once they'd crowd the logo, so the
           bar keeps to logo + Log in rather than wrapping onto two rows.
           Below 820px the inline links move into the burger menu. */
        .mq-burger { display: none; }
        /* The open menu panel has its own class: sharing the button's
           inline-flex made it shrink to its widest label, so the rules under
           the links stopped short of the screen edge. */
        .mq-burgerpanel { display: none; }
        @media (max-width: 820px) {
            .mq-navitem { display: none; }
            .mq-nav { gap: 12px; }
            .mq-burger { display: inline-flex; }
            .mq-burgerpanel { display: flex; }
        }
        /* A longer link list (the challenge page's sections) needs more room
           before it fits on one row, so it moves into the burger sooner. */
        .mq-navitem { white-space: nowrap; }
        @media (max-width: 1100px) {
            .mq-nav--long .mq-navitem { display: none; }
            .mq-nav--long { gap: 12px; }
            .mq-nav--long .mq-burger { display: inline-flex; }
            .mq-burgerpanel--long { display: flex; }
        }

        /* "Log in" was breaking onto two lines on narrow phones: the pill is a
           flex item, so it was allowed to shrink and wrap its own label. Pin it
           to one line and stop it shrinking, then buy back the width it needs by
           tightening the bar's padding and gap, which pushes the logo further
           left and the pill further right. */
        .mq-navpill { white-space: nowrap; flex-shrink: 0; }
        @media (max-width: 420px) {
            .mq-headrow { padding-left: 12px !important; padding-right: 12px !important; gap: 8px !important; }
            .mq-nav { gap: 6px !important; }
            .mq-navpill { padding-left: 16px !important; padding-right: 16px !important; }
        }

        /* Burger panel: the rules ran only as wide as the padded column. Padding
           moved onto the links themselves so each line spans the full width, and
           the last link drops its rule so the panel doesn't end on one. */
        .mq-menulink:last-child { border-bottom: none !important; }

        /* Challenge banner button: darkens and lifts on hover. No arrow, which
           widened it and pushed it onto a second line. */
        .mq-chbanner-btn { transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease; }
        .mq-chbanner:hover .mq-chbanner-btn {
            background: ${C.darkest} !important;
            transform: translateY(-2px);
            box-shadow: 0 6px 14px -6px rgba(10,22,16,0.5) !important;
        }
    `}</style>
);

// The banner counts down to, and goes at, the close of sign-ups (midnight, end of
// day 1, CHALLENGE_COHORT_SIGNUP_CLOSES_AT in server.py).
const CHALLENGE_STARTS = new Date(CHALLENGE_CLOSES);

// A strip above the header on every public page except the challenge page
// itself, not a popup: it scrolls away with the page and the whole strip is
// the link (the button is a span, so nothing clickable is nested inside
// another).
const ChallengeBanner = () => {
    const t = useCountdown(CHALLENGE_STARTS);
    if (t.done) return null;
    const pad = (n) => String(n).padStart(2, '0');
    const cells = [[t.days, 'd'], [pad(t.hours), 'h'], [pad(t.minutes), 'm'], [pad(t.seconds), 's']];
    return (
        <Link
            to="/challenge"
            className="mq-chbanner"
            style={{ display: 'block', background: '#b8ecce', color: C.deep, textDecoration: 'none', padding: '12px 24px', fontFamily: F.body, fontSize: 14, lineHeight: 1.4, textAlign: 'center' }}
        >
            {/* No wider than the page's own content (1160 less its gutters). */}
            <span style={{ maxWidth: 1112, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '10px 12px' }}>
                <span>
                    <span style={{ fontWeight: 800, textTransform: 'uppercase', marginRight: 6 }}>🔥 Join me in November:</span>
                    <strong>30-Day French Speaking & Pronunciation Challenge</strong>
                </span>
                <span role="timer" aria-label={`${t.days} days ${t.hours} hours until the challenge starts`} style={{ display: 'inline-flex', gap: 4 }}>
                    {cells.map(([v, unit]) => (
                        <span key={unit} style={{ background: '#ffffff', borderRadius: 8, padding: '3px 6px', fontFamily: F.brand, fontWeight: 800, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{v}{unit}</span>
                    ))}
                </span>
                {/* Dark green, so it can't be mistaken for the header's Log in. */}
                <span className="mq-chbanner-btn" style={{ ...CTA_PRIMARY, background: C.deep, fontSize: 14, padding: '8px 18px', whiteSpace: 'nowrap' }}>
                    Grab your place
                </span>
            </span>
        </Link>
    );
};

/**
 * Public site header.
 *
 * `onLogin` is passed by the landing page, which opens its auth modal in place.
 * Pages without that modal (the legal pages) leave it off and the button sends
 * people to the landing page with ?auth=login, which opens the modal on
 * arrival.
 *
 * `home` marks the landing page itself, where the section links are in-page
 * anchors and the logo scrolls to the top rather than navigating.
 */
// `cta` is for a sales page that must not send anyone elsewhere (the challenge
// page): no login, the logo only scrolls to the top, and one button,
// { label, onClick, links }, in place of the login. `links` are that page's
// own sections, [id, label] pairs, scrolled to in place like home's.
export const SiteHeader = ({ onLogin, home = false, cta = null }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const inPage = home || !!cta;
    const links = cta ? (cta.links || []) : NAV_LINKS;
    const hrefFor = (id) => (inPage ? `#${id}` : `/#${id}`);
    // Logged in, the button takes you back into the app instead of offering a
    // login. Hidden (not swapped) until the session is known, so a logged-in
    // reader never sees "Log in" flash up first.
    const { user, loading } = useAuth();

    return (
        <>
        {/* Not on the challenge page (the only one with `cta`): it is the
            page the banner points to. */}
        {!cta && <ChallengeBanner />}
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: C.darkest, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <ChromeStyles />
            <div className="mq-headrow" style={{ maxWidth: 1160, margin: '0 auto', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                {home || cta ? (
                    <a
                        href="#top"
                        aria-label="Mimique"
                        onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        <MimiqueLockup on="dark" iconSize={44} fontSize={24} variant="bare" gap={8} />
                    </a>
                ) : (
                    <Link to="/" aria-label="Mimique" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        <MimiqueLockup on="dark" iconSize={44} fontSize={24} variant="bare" gap={8} />
                    </Link>
                )}

                <div className={cta ? 'mq-nav mq-nav--long' : 'mq-nav'} style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    {links.map(([id, label, route]) => (route ? (
                        <Link
                            key={id}
                            className="mq-navlink mq-navitem"
                            to={route}
                            style={{ fontSize: 14, fontWeight: 600, color: C.onDarkPrimary, textDecoration: 'none' }}
                        >
                            {label}
                        </Link>
                    ) : (
                        <a
                            key={id}
                            className="mq-navlink mq-navitem"
                            href={hrefFor(id)}
                            onClick={inPage ? (e) => { e.preventDefault(); scrollToSection(id); } : undefined}
                            style={{ fontSize: 14, fontWeight: 600, color: C.onDarkPrimary, textDecoration: 'none' }}
                        >
                            {label}
                        </a>
                    )))}
                    {cta ? (
                        <button
                            className="mq-navpill mq-arrowbtn"
                            onClick={cta.onClick}
                            style={{ ...CTA_PRIMARY, fontSize: 14, padding: '10px 22px', boxShadow: 'none' }}
                        >
                            {cta.label}<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                        </button>
                    ) : user ? (
                        <Link
                            className="mq-navpill mq-arrowbtn"
                            data-testid="dashboard-btn"
                            to="/dashboard"
                            style={{ ...CTA_PRIMARY, fontSize: 14, padding: '10px 22px', boxShadow: 'none', textDecoration: 'none' }}
                        >
                            Dashboard<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                        </Link>
                    ) : onLogin ? (
                        <button
                            className="mq-navpill mq-arrowbtn"
                            data-testid="login-btn"
                            onClick={onLogin}
                            style={{ ...CTA_PRIMARY, fontSize: 14, padding: '10px 22px', boxShadow: 'none', visibility: loading ? 'hidden' : 'visible' }}
                        >
                            Log in<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                        </button>
                    ) : (
                        <Link
                            className="mq-navpill mq-arrowbtn"
                            data-testid="login-btn"
                            to="/?auth=login"
                            style={{ ...CTA_PRIMARY, fontSize: 14, padding: '10px 22px', boxShadow: 'none', textDecoration: 'none', visibility: loading ? 'hidden' : 'visible' }}
                        >
                            Log in<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                        </Link>
                    )}
                    {links.length > 0 && <button
                        className="mq-burger"
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(v => !v)}
                        style={{ border: 'none', background: 'transparent', color: C.onDarkPrimary, cursor: 'pointer', padding: 6, alignItems: 'center' }}
                    >
                        {menuOpen ? <CloseIcon size={24} /> : <Menu size={24} />}
                    </button>}
                </div>
            </div>

            {/* Burger panel — only rendered under the 820px breakpoint */}
            {menuOpen && (
                <div className={cta ? 'mq-burgerpanel mq-burgerpanel--long' : 'mq-burgerpanel'} style={{ flexDirection: 'column', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '8px 0 16px' }}>
                    {links.map(([id, label, route]) => {
                        const linkStyle = { display: 'block', padding: '12px 24px', fontSize: 15, fontWeight: 600, color: C.onDarkPrimary, textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)' };
                        return route ? (
                            <Link key={id} className="mq-menulink" to={route} onClick={() => setMenuOpen(false)} style={linkStyle}>
                                {label}
                            </Link>
                        ) : (
                            <a
                                key={id}
                                className="mq-menulink"
                                href={hrefFor(id)}
                                onClick={(e) => {
                                    setMenuOpen(false);
                                    if (!inPage) return;
                                    e.preventDefault();
                                    scrollToSection(id);
                                }}
                                style={linkStyle}
                            >
                                {label}
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
        </>
    );
};

/**
 * The band that closes a page: headline, one button, logo watermark.
 *
 * The landing page ends with it and so does every blog post, so it lives here
 * rather than being written twice. `onCta` is for the landing page, which
 * opens its sign-up modal in place; `to` is for pages without that modal,
 * which send people to the landing page instead.
 */
export const SiteFinalCta = ({
    heading = 'Ready to speak French confidently with Mimique?',
    label = 'Try Mimique for free',
    onCta,
    to,
    compact = false,
}) => {
    const { user } = useAuth();
    const dest = to || (user ? '/dashboard' : '/?auth=signup');
    return (
    <div
        className="mq-section"
        style={{
            background: `linear-gradient(160deg, ${C.primary} 0%, ${C.deep} 70%, ${C.darkest} 100%)`,
            padding: compact ? '90px 24px' : '130px 24px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        <div className="mq-motif" aria-hidden="true">
            <MimiqueIcon size={460} variant="bare" />
        </div>
        <div className="mq-finalcta" style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26, position: 'relative', zIndex: 1 }}>
            <h2 style={{ margin: 0, fontFamily: F.heading, fontSize: compact ? 32 : 36, fontWeight: 700, color: '#fff' }}>
                {heading}
            </h2>
            {onCta ? (
                <button className="mq-cta mq-arrowbtn" onClick={onCta} style={{ ...CTA_PRIMARY, padding: '15px 38px' }}>
                    {label}<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                </button>
            ) : (
                <Link className="mq-cta mq-arrowbtn" to={dest} style={{ ...CTA_PRIMARY, padding: '15px 38px', textDecoration: 'none' }}>
                    {label}<span className="mq-arrow" aria-hidden="true">&rarr;</span>
                </Link>
            )}
        </div>
    </div>
    );
};

/** Public site footer. Identical on every public page. */
export const SiteFooter = () => {
    const { pathname } = useLocation();
    return (
    <div style={{ background: C.darkest, borderTop: '1px solid rgba(255,255,255,0.08)', padding: '64px 24px 40px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28, alignItems: 'center' }}>
            {/* Always takes you home; on the home page it scrolls to the top. */}
            <Link
                className="mq-cta"
                to="/"
                aria-label={pathname === '/' ? 'Back to top' : 'Mimique home'}
                onClick={(e) => {
                    if (pathname === '/') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        window.scrollTo(0, 0);
                    }
                }}
                style={{ cursor: 'pointer', lineHeight: 0 }}
            >
                <MimiqueIcon size={52} variant="square" />
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                {SOCIALS.map(({ label, href, icon }) => (
                    <a
                        key={label}
                        className="mq-social"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', fontSize: 13.5, fontWeight: 600, color: C.onDarkPrimary, textDecoration: 'none' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">{icon}</svg>
                        {label}
                    </a>
                ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                <Link className="mq-footlink" to="/legal/mentions-legales" style={{ fontSize: 13, color: C.footerLink, textDecoration: 'none' }}>Mentions légales</Link>
                <Link className="mq-footlink" to="/legal/terms" style={{ fontSize: 13, color: C.footerLink, textDecoration: 'none' }}>Terms of Use and Sale</Link>
                <Link className="mq-footlink" to="/legal/privacy" style={{ fontSize: 13, color: C.footerLink, textDecoration: 'none' }}>Privacy Policy</Link>
            </div>
            <a href="https://elevenlabs.io/startup-grants" target="_blank" rel="noopener noreferrer">
                <img src="https://eleven-public-cdn.elevenlabs.io/payloadcms/cy7rxce8uki-IIElevenLabsGrants%201.webp" alt="ElevenLabs Grants" style={{ width: 160 }} />
            </a>
            <div style={{ paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.07)', fontSize: 12.5, color: '#5c8a70', textAlign: 'center', width: '100%' }}>
                © {new Date().getFullYear()} Mimique. Made with 💚 by <a className="mq-footlink" href="https://leanneoleary.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#5c8a70', textDecoration: 'underline' }}>Leanne O'Leary</a>.
            </div>
        </div>
    </div>
    );
};
